import React from 'react';
import toastr from 'react-hot-toast';
import moment from 'moment';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useWorkspaceContext } from 'hooks/common/useWorkspaceContext';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { formatToISOString } from 'utilities/time';
import type {
  SparkRunsChartCoordinate,
  UsageStatisticsResponse,
} from 'types/HistoryChartData';
import type { HistoryFilter } from 'components/History';

export function useAddChartData(historyFilter: HistoryFilter | null) {
  const { currentWorkspaceID } = useWorkspaceContext();

  const addChartData = () => {
    const body = {
      data: {
        type: SPARK_RESOURCE_TYPES.USAGE_STATISTICS,
        attributes: {
          workspaceID: currentWorkspaceID,
          sparks: historyFilter?.sparkIDs,
          startDate: formatToISOString(historyFilter?.dateRange.startDate),
          endDate: formatToISOString(historyFilter?.dateRange.endDate),
        },
      },
    };

    return fetchWrapper<UsageStatisticsResponse>({
      url: SPARK_RESOURCE_TYPES.USAGE_STATISTICS,
      method: 'POST',
      body: body,
    });
  };

  const response = useQuery(
    ['HistoryChartData', currentWorkspaceID, historyFilter],
    addChartData,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to post History chart data: ${error.message}`);
        }
      },
      ...CACHE_AND_STALE_TIME,
    }
  );
  const sortByDate = React.useCallback(
    (
      a: Partial<SparkRunsChartCoordinate>,
      b: Partial<SparkRunsChartCoordinate>
    ) => {
      const dateA = moment.utc(a.date, 'MMM YY');
      const dateB = moment.utc(b.date, 'MMM YY');
      return dateA.valueOf() - dateB.valueOf();
    },
    []
  );
  const sparkRunsChartData = React.useMemo(
    () => response.data?.data.attributes.sparkRuns.sort(sortByDate),
    [response.data?.data.attributes.sparkRuns, sortByDate]
  );
  const creditsUsedChartData = React.useMemo(
    () => response.data?.data.attributes.creditsUsed.sort(sortByDate),
    [response.data?.data.attributes.creditsUsed, sortByDate]
  );

  const totals = response.data?.data.attributes.totals;
  const timeUnit = response.data?.data.attributes.timeUnit;

  return {
    ...response,
    sparkRunsChartData,
    creditsUsedChartData,
    totals,
    timeUnit,
  };
}
